import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { api, ensureOk } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Paginador } from '@/components/Paginador';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { fechaHora } from '@/lib/formato';

import { useReportes } from './queries';

const CLAVES = ['status'] as const;

function Reportes() {
  const { filtros, cambiar } = useFiltrosUrl(CLAVES);
  const editable = useCan('preguntas', 'editar');
  const qc = useQueryClient();
  const status = (filtros.status || 'open') as 'open' | 'closed';
  const q = useReportes({ page: filtros.page, status });
  const cambiarEstado = useMutation({
    mutationFn: ({ id, s }: { id: number; s: 'open' | 'closed' }) =>
      ensureOk(
        api.PATCH('/api/v1/admin/question-reports/{id}', {
          params: { path: { id: String(id) } },
          body: { status: s },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reportes'] });
      toast.success('Reporte actualizado.');
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  return (
    <>
      <Encabezado
        titulo="Reportes de alumnos"
        descripcion="Errores que los alumnos señalan en las preguntas."
      />
      <div className="mb-4 flex gap-2">
        {(['open', 'closed'] as const).map((s) => (
          <button
            key={s}
            type="button"
            className={`btn-cs ${status === s ? 'btn-primario' : 'btn-vidrio'}`}
            onClick={() => cambiar({ status: s === 'open' ? '' : s })}
          >
            {s === 'open' ? 'Abiertos' : 'Cerrados'}
          </button>
        ))}
      </div>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.data.length === 0 && (
        <Vacio titulo={status === 'open' ? 'Sin reportes abiertos' : 'Sin reportes cerrados'} />
      )}
      {q.data && q.data.data.length > 0 && (
        <div className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Pregunta</th>
                <th>Problema</th>
                <th>Alumno</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.data.map((r) => (
                <tr key={r.id}>
                  <td className="whitespace-nowrap">{fechaHora(r.createdAt)}</td>
                  <td className="max-w-md">
                    <Link to={`/preguntas/${r.questionId}`} className="font-semibold">
                      #{r.questionId}
                    </Link>{' '}
                    {r.questionPrompt}
                  </td>
                  <td className="max-w-md">
                    <strong>{r.issue ?? 'Otro'}</strong>
                    {r.comment && <p className="text-texto-2">{r.comment}</p>}
                  </td>
                  <td>
                    <Link to={`/alumnos/${r.userId}`}>{r.userName}</Link>
                    <p className="text-xs text-texto-3">{r.userEmail}</p>
                  </td>
                  <td className="text-right">
                    {editable && (
                      <button
                        type="button"
                        className="btn-cs btn-contorno px-2 py-1 text-xs"
                        disabled={cambiarEstado.isPending}
                        onClick={() =>
                          cambiarEstado.mutate({
                            id: r.id,
                            s: r.status === 'open' ? 'closed' : 'open',
                          })
                        }
                      >
                        {r.status === 'open' ? 'Cerrar' : 'Reabrir'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.data && (
        <Paginador
          page={q.data.page}
          pageSize={q.data.pageSize}
          total={q.data.total}
          onPage={(page) => cambiar({ page })}
        />
      )}
    </>
  );
}

export function ReportesPage() {
  return (
    <PermissionGate seccion="preguntas">
      <Reportes />
    </PermissionGate>
  );
}
